import { useState } from "react";
import { requestMagicLink, verifyCode, ApiError } from "../api";

interface Props {
  onSignedIn: () => void;
  onShowGettingStarted?: () => void;
}

type Stage = "email" | "code";

export function SignIn({ onSignedIn, onShowGettingStarted }: Props) {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [stage, setStage] = useState<Stage>("email");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onRequest(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await requestMagicLink(email.trim().toLowerCase(), window.location.origin);
      setStage("code");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to send code.");
    } finally {
      setSubmitting(false);
    }
  }

  async function onVerify(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await verifyCode(email.trim().toLowerCase(), code.trim());
      onSignedIn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't verify code.");
    } finally {
      setSubmitting(false);
    }
  }

  if (stage === "code") {
    return (
      <div className="signin">
        <img className="logo" src="/apple-touch-icon.png" alt="" />
        <h1>Enter code</h1>
        <p>
          We sent a 6-digit code to <strong>{email}</strong>. Paste or type it
          below — the code expires in 15 minutes.
        </p>
        <form onSubmit={onVerify}>
          <input
            type="text"
            inputMode="numeric"
            pattern="\d{6}"
            maxLength={6}
            autoComplete="one-time-code"
            placeholder="123456"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            style={{
              textAlign: "center",
              fontFamily: "var(--mono)",
              fontSize: 20,
              letterSpacing: "0.3em",
            }}
            autoFocus
            required
          />
          <button type="submit" disabled={submitting || code.length !== 6}>
            {submitting ? "Verifying…" : "Sign in"}
          </button>
          {error && <div className="error">{error}</div>}
        </form>
        <p style={{ marginTop: 24, fontSize: 13 }}>
          <button
            type="button"
            className="secondary small"
            onClick={() => {
              setStage("email");
              setCode("");
              setError(null);
            }}
          >
            Use a different email
          </button>
        </p>
        <p className="muted" style={{ marginTop: 18, fontSize: 12 }}>
          Tip: the email also includes a one-tap sign-in link if you'd rather
          skip the code.
        </p>
      </div>
    );
  }

  return (
    <div className="signin">
      <img className="logo" src="/apple-touch-icon.png" alt="" />
      <h1>
        Trail Status<span className="pro">Pro</span>
      </h1>
      <p>Admin &amp; curator console. Enter your email — we'll send a code.</p>
      <form onSubmit={onRequest}>
        <input
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoFocus
        />
        <button type="submit" disabled={submitting || !email}>
          {submitting ? "Sending…" : "Send code"}
        </button>
        {error && <div className="error">{error}</div>}
      </form>
      {onShowGettingStarted && (
        <p style={{ marginTop: 24, fontSize: 13 }}>
          New here?{" "}
          <a
            href="/getting-started"
            onClick={(e) => { e.preventDefault(); onShowGettingStarted(); }}
          >
            Read the getting-started guide →
          </a>
        </p>
      )}
    </div>
  );
}
